import axios from "axios";
import { ApiResponse } from "shared/types/axios";

import { logErrorToSentry, sentryInstance } from "shared/helpers/utils";

import { updateCounters } from "shared/redux/slices/notify/counters";
import { updateBalance } from "shared/redux/slices/profile";

import { isAuthorized } from "./auth";

import store from "@storeEnv/appStore";

if (import.meta.env.VITE_APP_API_HOST !== "") {
  axios.defaults.withCredentials = true;
}

axios.interceptors.response.use(
  (response) => {
    if (response?.data?.success && response?.data?.notify) {
      store.dispatch(updateCounters(response.data.notify));

      if (response?.data?.balance !== undefined) {
        store.dispatch(updateBalance(+response.data.balance));
      }
    }

    return response;
  },
  (error) => {
    if (sentryInstance) {
      logErrorToSentry(error);
    }

    if (isAuthorized(error?.response)) {
      if (error?.data?.notify) {
        store.dispatch(updateCounters(error.data.notify));
      }

      if (error?.data?.balance !== undefined) {
        store.dispatch(updateBalance(+error.data.balance));
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Seng get request to url
 */
export function get<Type>(url: string, params: any | null = null) {
  return axios.get<{ data: Type; success: boolean }>(url, params);
}

/**
 * Seng post request to url
 */
export const post = <T>(
  url: string,
  attributes: any = null,
  headers: any = null
) => axios.post<{ data: T; success: boolean }>(url, attributes, headers);

/**
 * Seng put request to url
 */
export const put = (url: string, attributes: any = null, headers: any = null) =>
  axios.put(url, attributes, headers);

/**
 * Safe wrapper for non-critical requests
 * Returns fallback value instead of throwing on error
 */
export async function safeRequest<Type>(
  request: Promise<{ data: ApiResponse<Type> }>,
  fallback: Type
): Promise<Type> {
  try {
    const res = await request;

    if (!res.data?.success) {
      console.warn(
        "Non-critical request returned success = false",
        res.data?.errors
      );
      return fallback;
    }

    return res.data.data;
  } catch (error: unknown) {
    console.warn("Non-critical request failed:", error);
    return fallback;
  }
}
